<script setup lang="ts">
import { faX } from '@fortawesome/pro-light-svg-icons'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'

const props = defineProps<{
  episode: PodcastRssEpisode
}>()

const { $parseStringToDocument } = useNuxtApp()

const showDescription = ref(false)

const descriptionRef = useTemplateRef('descriptionRef')

const isExpandable = ref(false)

useResizeObserver(descriptionRef, async () => {
  await nextTick()
  const el = descriptionRef.value
  isExpandable.value = !!el && el.clientHeight < el.scrollHeight
})

const audio = useTemplateRef('audio')

const description = computed(
  () => $parseStringToDocument(props.episode.content, [removeAcastDisclaimer, 'remove-empty-nodes']).body.innerHTML,
)

const shadowOpacity = ref(1)

function toggleDescription(event: Event) {
  event.preventDefault()
  showDescription.value = event.target instanceof HTMLDetailsElement
    ? event.target.open
    : !showDescription.value

  shadowOpacity.value = 0
  setTimeout(() => {
    shadowOpacity.value = 1
  }, 200)
}
</script>

<template>
  <component
    :is="$attrs.is ?? 'li'"
    class="flex flex-col z-10 gap-2.5 [--card-padding:theme(spacing.2xs)] lg:[--card-padding:0] p-[--card-padding] rounded-xl lg:rounded-none bg-blurple-50 lg:bg-transparent"
  >
    <MediaControls v-slot="{ isPlaying }" :media="audio">
      <div class="flex gap-xs lg:gap-xl">
        <div class="flex flex-col gap-2.5">
          <PodcastEpisodeImage :episode="episode" />
          <PodcastEpisodeMetadata :episode="episode" class="bg-blurple-50 rounded-lg hidden lg:flex flex-col p-2xs gap-3xs" />
        </div>

        <div class="flex flex-col gap-s self-center lg:self-start">
          <PodcastEpisodeTitle :episode="episode" />

          <div
            ref="descriptionRef"
            class="hidden duration-300"
            :class="showDescription ? 'lg:line-clamp-none' : 'lg:line-clamp-4'"
            v-html="description"
          />

          <button v-if="isExpandable || showDescription" class="hidden lg:flex font-bold gap-3xs items-center hover:underline self-start" @click="toggleDescription">
            <Icon v-if="showDescription" :icon="faX" size="xs" class="p-1" />

            {{ showDescription ? 'Lukk info' : 'Les mer' }}
          </button>

          <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
          <audio ref="audio" :src="episode.enclosure.url" preload="none" />

          <div class="gap-xl hidden lg:flex">
            <MediaPlayButton class="flex gap-xs items-center" :aria-label="`${isPlaying ? 'Pause' : 'Spill av'} episode ${episode.number}`">
              <Shape
                variant="hexagon"
                class="size-14 hover:contrast-75 z-10"
                :icon="isPlaying ? faPause : faPlay"
                :class="isPlaying ? 'fill-moss-200 text-blurple-500' : 'fill-blurple-500 text-moss-200'"
              />

              {{ isPlaying ? 'Pause' : 'Spill av' }}
            </MediaPlayButton>
            <div v-if="isPlaying" class="flex gap-4 items-center w-full">
              <div class="flex gap-2 items-center w-full">
                <MediaTimestamp type="current" />
                <MediaTimeline theme="blurple" size="thin" />
                <MediaTimestamp type="duration" />
              </div>
              <MediaSeekButton :delta="-15" />
              <MediaSeekButton :delta="15" />
            </div>
          </div>
        </div>
      </div>

      <details class="lg:hidden" :open="showDescription" @toggle="toggleDescription">
        <summary class="group cursor-pointer">
          <div class="flex justify-between items-center relative">
            <PodcastEpisodeMetadata :episode="episode" class="items-center py-2xs px-3xs gap-2xs sm:gap-xs">
              <div class="font-bold flex gap-3xs items-center group-hover:underline self-start select-none text-blurple-400">
                <Icon v-if="showDescription" :icon="faX" size="xs" class="hidden sm:block p-1" />

                <span v-if="showDescription">
                  Lukk<span class="hidden @xs:inline">&nbsp;info</span>
                </span>
                <template v-else>
                  <span class="@xs:hidden">Les mer</span>
                  <span class="hidden @xs:inline">Om&nbsp;episoden</span>
                </template>
              </div>
            </PodcastEpisodeMetadata>

            <div :data-play-button-wrapper="showDescription ? 'open' : 'closed'">
              <MediaPlayButton class="flex gap-xs items-center" :aria-label="`${isPlaying ? 'Pause' : 'Spill av'} episode ${episode.number}`">
                <Shape
                  variant="hexagon"
                  class="size-14 hover:contrast-75 z-10"
                  :icon="isPlaying ? faPause : faPlay"
                  :class="isPlaying ? 'fill-moss-200 text-blurple-500' : 'fill-blurple-500 text-moss-200'"
                />
              </MediaPlayButton>
            </div>
          </div>
        </summary>

        <div class="flex flex-col gap-2 pb-xs">
          <div class="px-3xs relative" data-description v-html="description" />

          <button class="font-bold flex gap-3xs items-center hover:underline select-none" @click="toggleDescription">
            <Icon :icon="faX" size="xs" class="p-1" />

            Lukk info
          </button>
        </div>
      </details>
    </MediaControls>
  </component>
</template>

<style scoped lang="postcss">
/* Simple trick to make text wrap around the big play button on small devices */
[data-description]:before {
  @apply content-[''] float-right h-4 w-14;
}

[data-play-button-wrapper] {
  --padding: theme(spacing.3);
  --roundness: theme('borderRadius.2xl');
  --size: theme(size.7);
  --shadow-offset: calc(var(--size) / 2);
  --negative-size: calc(var(--size) * -1);
  transition:
    border-radius 0.2s,
    padding-bottom 0.2s;

  @apply -mb-[--card-padding] -mr-[--card-padding] bg-white lg:hidden pt-[--padding] pl-[--padding] relative rounded-tl-[--roundness];

  &:before,
  &:after,
  button:before {
    @apply content-[''] absolute size-[--size] z-0;
  }

  button:before {
    @apply right-0 rounded-tr-[--roundness] bottom-[--negative-size];
    box-shadow: 0 calc(var(--shadow-offset) * -1) 0 0 white;
  }

  &:before,
  &:after {
    box-shadow: 0 var(--shadow-offset) 0 0 white;
  }

  &:before {
    @apply rounded-br-[--roundness] bottom-0 left-[--negative-size] transition-opacity duration-0;
  }

  &:after {
    @apply rounded-br-[--roundness] right-0 top-[--negative-size];
  }
}

[data-play-button-wrapper='closed']:before {
  @apply opacity-100 delay-[180ms];
}

[data-play-button-wrapper='open'] {
  @apply pb-[--padding] rounded-bl-[--roundness];

  &:before {
    @apply opacity-0 delay-0;
  }
}
</style>
